import { AdminDroneFlightEntry } from '../data-models/admin-drone-flight-entry.model';
import { AdminSprayRecordSummary } from '../response-models/admin-spray-record-summary.model';
import { SprayRecordStatus } from '../data-models/spray-record-status.model';
import { isRecord } from './is-record';
import { toDroneFlightEntry } from './spray-record-wire-utils';
import { countOrZero, idString, listOf, nullableBoolean, nullableNumber, nullableString, stringArrayOrEmpty } from './wire-value-utils';

/**
 * Readers for the admin spray-record responses (hub `admin-spray-records-controller.ts`) where they add to the
 * shapes web reads (`spray-record-wire-utils.ts`, kept identical to web's).
 */

/** A drone flight from the detail: the shared entry plus `droneNNumber`. */
export function toAdminDroneFlightEntry(value: unknown): AdminDroneFlightEntry {
  const source = isRecord(value) ? value : {};
  return { ...toDroneFlightEntry(value), droneNNumber: nullableString(source['droneNNumber']) };
}

/** One row of `GET /admin/spray-records` (hub `SprayRecordService.listAll`). */
export function toAdminSprayRecordSummary(value: unknown): AdminSprayRecordSummary {
  const source = isRecord(value) ? value : {};
  return {
    id: idString(source['id']),
    organizationId: nullableString(source['organizationId']) ?? null,
    organizationName: nullableString(source['organizationName']) ?? null,
    fieldId: nullableString(source['fieldId']) ?? null,
    workOrderId: nullableString(source['workOrderId']) ?? null,
    applicationDate: nullableString(source['applicationDate']) ?? null,
    status: (nullableString(source['status']) ?? null) as SprayRecordStatus | null,
    certificationId: nullableString(source['certificationId']) ?? null,
    notes: nullableString(source['notes']) ?? null,
    weatherConditions: nullableString(source['weatherConditions']) ?? null,
    fieldName: nullableString(source['fieldName']) ?? null,
    cropType: nullableString(source['cropType']) ?? null,
    customerId: nullableString(source['customerId']) ?? null,
    customerName: nullableString(source['customerName']) ?? null,
    totalAcreage: countOrZero(source['totalAcreage']),
    droneCount: countOrZero(source['droneCount']),
    droneNames: stringArrayOrEmpty(source['droneNames']),
    pilotName: nullableString(source['pilotName']) ?? null,
    // `?? null`, not `|| null`: a genuine 0 would be lost by the latter.
    filedFlights: nullableNumber(source['filedFlights']) ?? null,
    needsFlightCount: nullableBoolean(source['needsFlightCount']) === true,
    chemicals: listOf(source['chemicals'], toChemicalSummary),
  };
}

function toChemicalSummary(value: unknown): { id: string; name: string; rate: string | null } {
  const source = isRecord(value) ? value : {};
  return { id: idString(source['id']), name: nullableString(source['name']) ?? '', rate: nullableString(source['rate']) ?? null };
}
